/**
 * The shared chrome the state overlays sit in, plus the English default copy they fall
 * back to. Copy is the consumer's (via `ReactNode` slots) — libs/ui owns only the machine
 * + structure, never an app i18n key; these defaults are the last resort when a consumer
 * passes no slot.
 */
import type { ReactNode } from 'react'

import { cn } from '@gto/ui/cn'

/** Copy + affordance slots for the overlays. All optional with English defaults; the app passes translated `ReactNode`s. */
export type DataTableStateCopy = {
  /**
   * Empty-state copy when data exists but the current filter matches nothing —
   * distinguished from `emptyMessage` via `hasAnyData` so a filtered-to-empty user
   * gets actionable copy.
   */
  noMatchMessage?: ReactNode
  /** Empty-state message when there is no data at all (no-data-at-all). */
  emptyMessage?: ReactNode
  /** Error-overlay message. */
  errorMessage?: ReactNode
  /** Accessible label + text for the error retry button. Defaults to "Retry". */
  retryLabel?: ReactNode
  /** Load-more inline error message. Defaults to "Failed to load more". */
  loadMoreErrorMessage?: ReactNode
  /** Load-more inline retry button label. Defaults to "Retry". */
  loadMoreRetryLabel?: ReactNode
}

export const DEFAULT_EMPTY_MESSAGE = 'No data'
export const DEFAULT_NO_MATCH_MESSAGE = 'No results match the current filters'
export const DEFAULT_ERROR_MESSAGE = 'Something went wrong'
export const DEFAULT_RETRY_LABEL = 'Retry'
export const DEFAULT_LOAD_MORE_ERROR_MESSAGE = 'Failed to load more'

/**
 * Centered overlay for the skeleton / empty / error. The preset renders it as a DOM
 * SIBLING of `TableRoot` (not inside `role="grid"`), so the error's Retry `<button>`
 * is never an orphaned non-row child of the grid (`aria-required-children`) and the
 * toolbar/filters stay reachable. `sticky`/`z-10` are inert under the preset's flex
 * layout — retained only for a bespoke consumer placing this in its own scroll container.
 */
export const StateOverlay = ({
  children,
  className,
  ...props
}: {
  children: ReactNode
  className?: string
} & Record<`data-${string}`, string>) => (
  <div
    className={cn(
      'sticky inset-x-0 top-0 z-10 flex min-h-32 flex-col items-center justify-center gap-3 px-6 py-8 text-center',
      className,
    )}
    {...props}
  >
    {children}
  </div>
)
